/**
 * Store operations for scene-level sketch state.
 *
 * Strokes and layers live at `AppState.sketch`, decoupled from any specific
 * SceneObject. Sketch mode entry no longer requires (or forces) a selection.
 *
 * Stroke state flow:
 *   beginStroke()   — push a fresh stroke onto the active layer; stash a
 *                     reference in `_inFlight` for hot-loop appends.
 *   addStrokePoint() — append a point in place, bump `sketchStrokeSeq`.
 *   endSketchStroke() — publish a fresh top-level sketch state (layer-shallow
 *                     clone) so undo equality checks see a new reference.
 */

import type {
  AppState,
  SketchToolType,
  SketchProjectionMode,
  SketchStrokePointInput,
} from "../app/storeTypes.ts";
import {
  cloneLayer,
  cloneSceneSketchState,
  createSketchLayer,
  isProjectedStroke,
  makeStrokeId,
  type SketchLayer,
  type SketchStroke,
  type ProjectedStroke,
  type ProjectedStrokePoint,
  type SceneSketchState,
  type ScreenspaceStroke,
  type ScreenspaceStrokePoint,
} from "../modeling/sketch.ts";

function strokeDebugEnabled(): boolean {
  return (
    (globalThis as { __STROKE_DEBUG__?: boolean }).__STROKE_DEBUG__ === true
  );
}

function logDebug(msg: string, data?: unknown): void {
  if (!strokeDebugEnabled()) return;
  console.log(`[sceneSketchOps] ${msg}`, data ?? "");
}

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

/** In-flight stroke reference for hot-loop appends. */
let _inFlight: {
  layerId: string;
  stroke: SketchStroke;
  startedAt: number;
  pressureMin: number;
  pressureMax: number;
  pressureSum: number;
  layerStrokeCountAtStart: number;
} | null = null;

let _layerStrokeColorEditLayerId: string | null = null;
let _layerStrokeColorUndoLayerId: string | null = null;

function pointHasFiniteValues(params: {
  p: [number, number, number];
  pressure: number;
  worldThickness?: number;
  skinIndex?: [number, number, number, number];
  skinWeight?: [number, number, number, number];
  corners?: [number, number, number];
  bary?: [number, number, number];
}): boolean {
  if (!Number.isFinite(params.pressure)) return false;
  if (
    params.worldThickness != null &&
    !Number.isFinite(params.worldThickness)
  ) {
    return false;
  }
  for (const v of params.p) {
    if (!Number.isFinite(v)) return false;
  }
  if (params.skinIndex)
    for (const v of params.skinIndex) if (!Number.isFinite(v)) return false;
  if (params.skinWeight)
    for (const v of params.skinWeight) if (!Number.isFinite(v)) return false;
  if (params.corners)
    for (const v of params.corners) if (!Number.isFinite(v)) return false;
  if (params.bary)
    for (const v of params.bary) if (!Number.isFinite(v)) return false;
  return true;
}

function summarizeSketchState(sketch: SceneSketchState): {
  layerCount: number;
  totalStrokes: number;
  totalPoints: number;
  largestStrokePoints: number;
} {
  let totalStrokes = 0;
  let totalPoints = 0;
  let largestStrokePoints = 0;
  for (const layer of sketch.layers) {
    totalStrokes += layer.strokes.length;
    for (const stroke of layer.strokes) {
      totalPoints += stroke.points.length;
      if (stroke.points.length > largestStrokePoints) {
        largestStrokePoints = stroke.points.length;
      }
    }
  }
  return {
    layerCount: sketch.layers.length,
    totalStrokes,
    totalPoints,
    largestStrokePoints,
  };
}

function makeProjectedPoint(
  params: SketchStrokePointInput,
): ProjectedStrokePoint {
  if (!params.attachment) {
    throw new Error("Projected sketch points require an attachment.");
  }
  return {
    attachment: {
      objectId: params.attachment.objectId,
      topologyVersion: params.attachment.topologyVersion,
    },
    p: params.p,
    pressure: params.pressure,
    skinIndex: params.skinIndex,
    skinWeight: params.skinWeight,
    corners: params.corners,
    bary: params.bary,
  };
}

function makeScreenspacePoint(
  params: SketchStrokePointInput,
): ScreenspaceStrokePoint {
  return {
    p: params.p,
    pressure: params.pressure,
  };
}

function refreshInFlightPressureStats(points: SketchStrokePointInput[]): void {
  if (!_inFlight || points.length === 0) return;
  let pressureMin = Number.POSITIVE_INFINITY;
  let pressureMax = Number.NEGATIVE_INFINITY;
  let pressureSum = 0;
  for (const point of points) {
    pressureMin = Math.min(pressureMin, point.pressure);
    pressureMax = Math.max(pressureMax, point.pressure);
    pressureSum += point.pressure;
  }
  _inFlight.pressureMin = pressureMin;
  _inFlight.pressureMax = pressureMax;
  _inFlight.pressureSum = pressureSum;
}

function withLayers(
  sketch: SceneSketchState,
  update: (layers: SketchLayer[]) => SketchLayer[],
): SceneSketchState {
  return { layers: update(sketch.layers) };
}

export function createSceneSketchOps(set: Set_, get: Get_) {
  const setSketch = (sketch: SceneSketchState) => set({ sketch });

  return {
    enterSketchMode: () => {
      // Auto-create "Layer 1" on first entry — every stroke needs a target.
      let sketch = get().sketch;
      if (sketch.layers.length === 0) {
        sketch = { layers: [createSketchLayer("Layer 1")] };
      }
      const activeLayerId = sketch.layers[sketch.layers.length - 1].id;
      set({
        sketch,
        sketchMode: {
          tool: "pencil",
          thickness: 6,
          color: "#ffffff",
          activeLayerId,
          projection: "projected",
        },
        mode: "navigate",
        editMode: null,
        poseMode: null,
        sculptMode: null,
        workspaceMode: "sketch",
      });
    },

    exitSketchMode: () => {
      _inFlight = null;
      _layerStrokeColorEditLayerId = null;
      _layerStrokeColorUndoLayerId = null;
      set({
        sketchMode: null,
        mode: "transform",
        workspaceMode: "object",
      });
    },

    setSketchTool: (tool: SketchToolType) => {
      const pm = get().sketchMode;
      if (!pm) return;
      set({ sketchMode: { ...pm, tool } });
    },

    setSketchThickness: (thickness: number) => {
      const pm = get().sketchMode;
      if (!pm) return;
      set({ sketchMode: { ...pm, thickness } });
    },

    setSketchColor: (hex: string) => {
      const pm = get().sketchMode;
      if (!pm) return;
      set({ sketchMode: { ...pm, color: hex } });
    },

    setSketchProjection: (projection: SketchProjectionMode) => {
      const pm = get().sketchMode;
      if (!pm) return;
      set({ sketchMode: { ...pm, projection } });
    },

    // -- Layer ops --------------------------------------------------------

    addSketchLayer: () => {
      const pm = get().sketchMode;
      if (!pm) return;
      get().pushUndo();
      const sketch = get().sketch;
      const layer = createSketchLayer(`Layer ${sketch.layers.length + 1}`);
      const next: SceneSketchState = { layers: [...sketch.layers, layer] };
      setSketch(next);
      set({ sketchMode: { ...pm, activeLayerId: layer.id } });
      logDebug("addSketchLayer", {
        layerId: layer.id,
        state: summarizeSketchState(next),
      });
    },

    deleteSketchLayer: (layerId: string) => {
      const pm = get().sketchMode;
      if (!pm) return;
      const sketch = get().sketch;
      if (sketch.layers.length <= 1) return;
      get().pushUndo();
      const next = withLayers(sketch, (layers) =>
        layers.filter((layer) => layer.id !== layerId),
      );
      const activeLayerId =
        pm.activeLayerId === layerId
          ? (next.layers[next.layers.length - 1]?.id ?? "")
          : pm.activeLayerId;
      setSketch(next);
      set({ sketchMode: { ...pm, activeLayerId } });
    },

    clearSketchLayer: (layerId: string) => {
      const pm = get().sketchMode;
      if (!pm) return;
      const sketch = get().sketch;
      const layer = sketch.layers.find((l) => l.id === layerId);
      if (!layer || layer.strokes.length === 0) return;
      get().pushUndo();
      const next = withLayers(sketch, (layers) =>
        layers.map((l) => (l.id === layerId ? { ...l, strokes: [] } : l)),
      );
      setSketch(next);
    },

    setActiveSketchLayer: (layerId: string) => {
      const pm = get().sketchMode;
      if (!pm) return;
      // No undo — the active layer cursor is transient UI state.
      set({ sketchMode: { ...pm, activeLayerId: layerId } });
    },

    renameSketchLayer: (layerId: string, name: string) => {
      const sketch = get().sketch;
      const layer = sketch.layers.find((l) => l.id === layerId);
      if (!layer || layer.name === name) return;
      get().pushUndo();
      const next = withLayers(sketch, (layers) =>
        layers.map((l) => (l.id === layerId ? { ...l, name } : l)),
      );
      setSketch(next);
    },

    setSketchLayerOpacity: (layerId: string, opacity: number) => {
      // Slider-driven — pushing undo per change would flood the stack.
      // Visual-only setting; users can adjust freely without undo entries.
      const sketch = get().sketch;
      const next = withLayers(sketch, (layers) =>
        layers.map((layer) =>
          layer.id === layerId ? { ...layer, opacity } : layer,
        ),
      );
      setSketch(next);
    },

    setSketchLayerVisible: (layerId: string, visible: boolean) => {
      const sketch = get().sketch;
      const layer = sketch.layers.find((l) => l.id === layerId);
      if (!layer || layer.visible === visible) return;
      get().pushUndo();
      const next = withLayers(sketch, (layers) =>
        layers.map((l) => (l.id === layerId ? { ...l, visible } : l)),
      );
      setSketch(next);
    },

    beginSketchLayerStrokeColorEdit: (layerId: string) => {
      _layerStrokeColorEditLayerId = layerId;
      _layerStrokeColorUndoLayerId = null;
    },

    setSketchLayerStrokeColor: (layerId: string, color: string) => {
      const sketch = get().sketch;
      const layer = sketch.layers.find((l) => l.id === layerId);
      if (!layer || layer.strokes.length === 0) return;
      if (layer.strokes.every((stroke) => stroke.color === color)) return;

      if (_layerStrokeColorEditLayerId === layerId) {
        if (_layerStrokeColorUndoLayerId !== layerId) {
          get().pushUndo();
          _layerStrokeColorUndoLayerId = layerId;
        }
      } else {
        get().pushUndo();
      }

      const next = withLayers(sketch, (layers) =>
        layers.map((l) =>
          l.id === layerId
            ? {
                ...l,
                strokes: l.strokes.map((stroke) => ({ ...stroke, color })),
              }
            : l,
        ),
      );
      setSketch(next);
    },

    endSketchLayerStrokeColorEdit: () => {
      _layerStrokeColorEditLayerId = null;
      _layerStrokeColorUndoLayerId = null;
    },

    moveSketchLayer: (layerId: string, direction: "up" | "down") => {
      const sketch = get().sketch;
      const layers = sketch.layers.slice();
      const index = layers.findIndex((layer) => layer.id === layerId);
      if (index < 0) return;
      const target =
        direction === "up"
          ? Math.min(layers.length - 1, index + 1)
          : Math.max(0, index - 1);
      if (target === index) return;
      get().pushUndo();
      const [layer] = layers.splice(index, 1);
      layers.splice(target, 0, layer);
      setSketch({ layers });
    },

    // -- Stroke capture ---------------------------------------------------

    beginStroke: (params: {
      p: [number, number, number];
      pressure: number;
      worldThickness: number;
      attachment?: { objectId: string; topologyVersion: number };
      fallbackPlane?: {
        point: [number, number, number];
        normal: [number, number, number];
      };
      skinIndex?: [number, number, number, number];
      skinWeight?: [number, number, number, number];
      corners?: [number, number, number];
      bary?: [number, number, number];
    }) => {
      const pm = get().sketchMode;
      if (!pm) return;

      // Resolve the active layer with a fall-back for stale ids.
      const sketch = cloneSceneSketchState(get().sketch);
      let layerIdx = sketch.layers.findIndex(
        (layer) => layer.id === pm.activeLayerId,
      );
      if (layerIdx < 0) layerIdx = sketch.layers.length - 1;
      const layer = sketch.layers[layerIdx];
      if (!layer) return;

      let stroke: SketchStroke;
      if (params.attachment) {
        const projected: ProjectedStroke = {
          kind: "projected",
          id: makeStrokeId(),
          tool: pm.tool,
          color: pm.color,
          thickness: pm.thickness,
          worldThickness: params.worldThickness,
          opacity: 1,
          fallbackPlane: params.fallbackPlane,
          points: [makeProjectedPoint(params)],
        };
        stroke = projected;
      } else {
        const screenspace: ScreenspaceStroke = {
          kind: "screenspace",
          id: makeStrokeId(),
          tool: pm.tool,
          color: pm.color,
          thickness: pm.thickness,
          worldThickness: params.worldThickness,
          opacity: 1,
          points: [
            {
              p: params.p,
              pressure: params.pressure,
            },
          ],
        };
        stroke = screenspace;
      }

      layer.strokes.push(stroke);

      _inFlight = {
        layerId: layer.id,
        stroke,
        startedAt: performance.now(),
        pressureMin: params.pressure,
        pressureMax: params.pressure,
        pressureSum: params.pressure,
        layerStrokeCountAtStart: layer.strokes.length - 1,
      };

      setSketch(sketch);
      set({
        sketchMode: { ...pm, activeLayerId: layer.id },
        sketchStrokeSeq: get().sketchStrokeSeq + 1,
      });
      logDebug("beginStroke", {
        kind: stroke.kind,
        strokeId: stroke.id,
        layerId: layer.id,
        tool: pm.tool,
        thickness: pm.thickness,
        worldThickness: params.worldThickness,
        finite: pointHasFiniteValues(params),
      });
    },

    addStrokePoint: (params: SketchStrokePointInput) => {
      if (!_inFlight) return;
      const stroke = _inFlight.stroke;
      if (isProjectedStroke(stroke)) {
        stroke.points.push(makeProjectedPoint(params));
      } else {
        stroke.points.push(makeScreenspacePoint(params));
      }
      _inFlight.pressureMin = Math.min(_inFlight.pressureMin, params.pressure);
      _inFlight.pressureMax = Math.max(_inFlight.pressureMax, params.pressure);
      _inFlight.pressureSum += params.pressure;
      // Hot loop: the stroke is mutated in place on the live sketch state;
      // bumping the seq forces overlay re-renders without cloning.
      set({ sketchStrokeSeq: get().sketchStrokeSeq + 1 });
    },

    replaceInFlightStrokePoints: (points: SketchStrokePointInput[]) => {
      if (!_inFlight || points.length === 0) return;
      const stroke = _inFlight.stroke;
      if (isProjectedStroke(stroke)) {
        stroke.points = points.map(makeProjectedPoint);
      } else {
        stroke.points = points.map(makeScreenspacePoint);
      }
      refreshInFlightPressureStats(points);
      set({ sketchStrokeSeq: get().sketchStrokeSeq + 1 });
    },

    eraseStrokes: (ids: Set<string>) => {
      if (ids.size === 0) return;
      const sketch = get().sketch;
      let anyChange = false;
      const nextLayers = sketch.layers.map((layer) => {
        const kept = layer.strokes.filter((s) => !ids.has(s.id));
        if (kept.length === layer.strokes.length) return layer;
        anyChange = true;
        return { ...layer, strokes: kept };
      });
      if (!anyChange) return;
      setSketch({ layers: nextLayers });
      set({ sketchStrokeSeq: get().sketchStrokeSeq + 1 });
    },

    endSketchStroke: () => {
      const finished = _inFlight;
      _inFlight = null;
      const pm = get().sketchMode;
      if (!pm) {
        set({ sketchStrokeSeq: get().sketchStrokeSeq + 1 });
        return;
      }
      // Publish a fresh top-level sketch reference so equality checks see a
      // new identity. Only the layer that owns the in-flight stroke needs a
      // shallow re-clone; the others reuse their existing references.
      const sketch = get().sketch;
      const layers = sketch.layers.map((layer) =>
        finished && layer.id === finished.layerId ? cloneLayer(layer) : layer,
      );
      setSketch({ layers });
      set({ sketchStrokeSeq: get().sketchStrokeSeq + 1 });
      logDebug("endSketchStroke", {
        strokeId: finished?.stroke.id,
        kind: finished?.stroke.kind,
        durationMs: finished
          ? Math.round(performance.now() - finished.startedAt)
          : 0,
        state: summarizeSketchState(get().sketch),
      });
    },
  };
}
